import { useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import {
  type ExportFormat,
  getMyExport,
  getTenantExport,
  requestMyExport,
  requestTenantExport,
} from '../api/exports';
import { usePreparedDownload } from '../hooks/usePreparedDownload';
import { PreparedDownloadNote } from './PreparedDownloadNote';

/**
 * "Export data" action (DATA-EXPORT). The user picks ONE format (CSV or JSON)
 * from the menu; it applies to all the data. Requests an async build, polls
 * until the zip is on S3, then saves it (`usePreparedDownload`). Every click
 * builds afresh: the data changes as sessions come in, and reusing the last
 * build handed out the first export's data forever. Scope:
 *   - `me`       → the caller's own data across every procedure
 *   - a tenantId → the whole tenant (tenant_admin), all participants
 */
export function ExportDataButton({
  scope,
  label = 'Export data',
  size = 'small',
  variant = 'outlined',
}: {
  scope: 'me' | { tenantId: string };
  label?: string;
  size?: 'small' | 'medium';
  variant?: 'text' | 'outlined' | 'contained';
}) {
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const request = (fmt: ExportFormat) => (scope === 'me'
    ? requestMyExport(fmt)
    : requestTenantExport(scope.tenantId, fmt));
  const status = (fmt: ExportFormat) => (scope === 'me'
    ? getMyExport(fmt)
    : getTenantExport(scope.tenantId, fmt));

  const exp = usePreparedDownload<ExportFormat>({
    fetchStatus: status, request, noun: 'export', reuseExisting: false,
  });
  const pendingFormat = exp.pending?.arg ?? null;
  const start = (fmt: ExportFormat) => {
    setMenuEl(null);
    void exp.start(fmt);
  };

  const pending = pendingFormat !== null;

  return (
    <>
      <PreparedDownloadNote error={exp.error} done={exp.done} />
      <Button
        size={size}
        variant={variant}
        startIcon={pending ? <CircularProgress size={14} /> : <DownloadIcon />}
        endIcon={pending ? undefined : <ArrowDropDownIcon />}
        onClick={(e) => setMenuEl(e.currentTarget)}
        disabled={pending}
      >
        {pending ? `Preparing ${pendingFormat?.toUpperCase()} export…` : label}
      </Button>
      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
        <MenuItem onClick={() => start('csv')}>
          <ListItemText primary="CSV (.zip)" secondary="Spreadsheet-friendly" />
        </MenuItem>
        <MenuItem onClick={() => start('json')}>
          <ListItemText primary="JSON (.zip)" secondary="Nested, machine-readable" />
        </MenuItem>
      </Menu>
    </>
  );
}
